import { useQueries } from '@tanstack/react-query';
import { useRef } from 'react';
import { useAuth } from '../../contexts/authContext';
import { documentsApi } from '../../lib/api/documentsApi.connect';
import { documentsClient } from '../../lib/connect/clients';
export async function readEvidenceSource(documentId) {
    const document = await documentsApi.getDocument(documentId);
    const text = await documentsClient.getDocumentText({
        documentId,
        offset: 0,
        limit: 4000
    });
    return {
        document,
        text: text.text,
        total: text.totalCharacters
    };
}
export function evidenceSourceKey(userId, tenantId, id) {
    return [
        'evidence-office-source',
        userId,
        tenantId,
        id
    ];
}
// Conversation access alone does not reauthorize retained document text. Keep
// dependencies across source changes; never erase the transcript or draft to hide it.
export function useConversationSourceAccess(messages, sourceId) {
    const { user } = useAuth();
    const ids = useRef(new Set());
    if (sourceId) ids.current.add(sourceId);
    function collect(value) {
        if (!value || typeof value !== 'object') return;
        for (const [key, child] of Object.entries(value)){
            if ((key === 'documentId' || key === 'document_id') && typeof child === 'string' && child) {
                ids.current.add(child);
            } else collect(child);
        }
    }
    for (const message of messages){
        for (const part of message.parts){
            if (part.type === 'text') {
                // contextPrompt stores its typed context as one JSON line in the user turn.
                for (const line of part.text.split('\n')){
                    try {
                        const context = JSON.parse(line);
                        if (context.workspace && context.record && context.source?.id) ids.current.add(context.source.id);
                    } catch  {
                    /* Ordinary conversation text is not context metadata. */ }
                }
            } else collect(part);
        }
    }
    const checks = useQueries({
        queries: [
            ...ids.current
        ].map((id)=>({
                queryKey: evidenceSourceKey(user?.id, user?.tenant_id, id),
                queryFn: ()=>readEvidenceSource(id),
                retry: false,
                staleTime: 0,
                refetchOnMount: 'always'
            }))
    });
    return {
        blocked: checks.some((check)=>check.isError || !check.data || check.isFetching),
        retry: ()=>{
            for (const check of checks)void check.refetch();
        }
    };
}
